/**
 * qPCR（リアルタイムPCR）遺伝子発現量測定の計算関数
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';
import { calculateBasicStatistics } from './basicStats';
import { unpairedTTest, type TTestResult } from './tTest';

/**
 * ΔCt法の結果
 */
export interface DeltaCtResult {
  samples: Array<{
    sampleName: string;
    housekeepingCt: number;
    targetCt: number;
    deltaCt: number;
    relativeExpression: number; // 2^(-ΔCt)
  }>;
  statistics: {
    mean: number;
    sd: number;
    se: number;
    confidenceInterval: {
      lower: number;
      upper: number;
    };
  };
}

/**
 * ΔΔCt法の結果
 */
export interface DeltaDeltaCtResult {
  control: {
    samples: Array<{
      sampleName: string;
      housekeepingCt: number;
      targetCt: number;
      deltaCt: number;
      relativeExpression: number; // 2^(-ΔCt)
    }>;
    statistics: {
      mean: number;
      sd: number;
      se: number;
      confidenceInterval: {
        lower: number;
        upper: number;
      };
    };
  };
  treatment: {
    samples: Array<{
      sampleName: string;
      housekeepingCt: number;
      targetCt: number;
      deltaCt: number;
      deltaDeltaCt: number;
      relativeExpression: number; // 2^(-ΔΔCt)
    }>;
    statistics: {
      mean: number;
      sd: number;
      se: number;
      confidenceInterval: {
        lower: number;
        upper: number;
      };
    };
  };
  tTest: TTestResult | null;
}

/**
 * ΔCt法による遺伝子発現量測定
 * 
 * @param housekeepingSamples ハウスキーピングジーンのサンプルデータ
 * @param targetSamples 標的遺伝子のサンプルデータ
 * @returns ΔCt法の結果
 */
export function deltaCtMethod(
  housekeepingSamples: Array<{ sampleName: string; ctValue: number }>,
  targetSamples: Array<{ sampleName: string; ctValue: number }>
): DeltaCtResult {
  // 入力検証
  if (housekeepingSamples.length === 0 || targetSamples.length === 0) {
    throw new KeisukeAdapterError('emptySampleData');
  }

  if (housekeepingSamples.length !== targetSamples.length) {
    throw new KeisukeAdapterError('qpcrSampleCountMismatch');
  }

  // サンプル名のマッチング（順序に依存）
  const samples: Array<{
    sampleName: string;
    housekeepingCt: number;
    targetCt: number;
    deltaCt: number;
    relativeExpression: number;
  }> = [];

  for (let i = 0; i < housekeepingSamples.length; i++) {
    const housekeeping = housekeepingSamples[i];
    const target = targetSamples[i];

    // ΔCt = Ct（標的遺伝子）- Ct（ハウスキーピングジーン）
    const deltaCt = target.ctValue - housekeeping.ctValue;

    // 相対発現量 = 2^(-ΔCt)
    const relativeExpression = Math.pow(2, -deltaCt);

    samples.push({
      sampleName: target.sampleName,
      housekeepingCt: housekeeping.ctValue,
      targetCt: target.ctValue,
      deltaCt,
      relativeExpression,
    });
  }

  // 相対発現量の統計計算
  const relativeExpressions = samples.map((s) => s.relativeExpression);
  const stats = calculateBasicStatistics(relativeExpressions);

  return {
    samples,
    statistics: {
      mean: stats.mean,
      sd: stats.stdDev,
      se: stats.stdError,
      confidenceInterval: stats.confidenceInterval,
    },
  };
}

/**
 * ΔΔCt法による遺伝子発現量測定
 * 
 * @param controlHousekeeping コントロール群のハウスキーピングジーンデータ
 * @param controlTarget コントロール群の標的遺伝子データ
 * @param treatmentHousekeeping 処理群のハウスキーピングジーンデータ
 * @param treatmentTarget 処理群の標的遺伝子データ
 * @returns ΔΔCt法の結果
 */
export function deltaDeltaCtMethod(
  controlHousekeeping: Array<{ sampleName: string; ctValue: number }>,
  controlTarget: Array<{ sampleName: string; ctValue: number }>,
  treatmentHousekeeping: Array<{ sampleName: string; ctValue: number }>,
  treatmentTarget: Array<{ sampleName: string; ctValue: number }>
): DeltaDeltaCtResult {
  // 入力検証
  if (
    controlHousekeeping.length === 0 ||
    controlTarget.length === 0 ||
    treatmentHousekeeping.length === 0 ||
    treatmentTarget.length === 0
  ) {
    throw new KeisukeAdapterError('emptySampleData');
  }

  if (
    controlHousekeeping.length !== controlTarget.length ||
    treatmentHousekeeping.length !== treatmentTarget.length
  ) {
    throw new KeisukeAdapterError('qpcrSampleCountMismatch');
  }

  // コントロール群のΔCt計算
  const controlSamples: Array<{
    sampleName: string;
    housekeepingCt: number;
    targetCt: number;
    deltaCt: number;
    relativeExpression: number;
  }> = [];

  for (let i = 0; i < controlHousekeeping.length; i++) {
    const housekeeping = controlHousekeeping[i];
    const target = controlTarget[i];

    const deltaCt = target.ctValue - housekeeping.ctValue;
    // コントロール群の相対発現量 = 2^(-ΔCt)（ただし、正規化の基準として使用）
    const relativeExpression = Math.pow(2, -deltaCt);

    controlSamples.push({
      sampleName: target.sampleName,
      housekeepingCt: housekeeping.ctValue,
      targetCt: target.ctValue,
      deltaCt,
      relativeExpression,
    });
  }

  // コントロール群の平均ΔCt
  const controlDeltaCts = controlSamples.map((s) => s.deltaCt);
  const controlMeanDeltaCt =
    controlDeltaCts.reduce((sum, val) => sum + val, 0) / controlDeltaCts.length;

  // 処理群のΔCt計算
  const treatmentSamples: Array<{
    sampleName: string;
    housekeepingCt: number;
    targetCt: number;
    deltaCt: number;
    deltaDeltaCt: number;
    relativeExpression: number;
  }> = [];

  for (let i = 0; i < treatmentHousekeeping.length; i++) {
    const housekeeping = treatmentHousekeeping[i];
    const target = treatmentTarget[i];

    const deltaCt = target.ctValue - housekeeping.ctValue;
    // ΔΔCt = ΔCt（処理群）- ΔCt（コントロール群の平均）
    const deltaDeltaCt = deltaCt - controlMeanDeltaCt;
    // 相対発現量 = 2^(-ΔΔCt)
    const relativeExpression = Math.pow(2, -deltaDeltaCt);

    treatmentSamples.push({
      sampleName: target.sampleName,
      housekeepingCt: housekeeping.ctValue,
      targetCt: target.ctValue,
      deltaCt,
      deltaDeltaCt,
      relativeExpression,
    });
  }

  // 統計計算
  const controlRelativeExpressions = controlSamples.map((s) => s.relativeExpression);
  const controlStats = calculateBasicStatistics(controlRelativeExpressions);

  const treatmentRelativeExpressions = treatmentSamples.map((s) => s.relativeExpression);
  const treatmentStats = calculateBasicStatistics(treatmentRelativeExpressions);

  // t検定（コントロール群と処理群の相対発現量の比較）
  let tTestResult: TTestResult | null = null;
  try {
    tTestResult = unpairedTTest(controlRelativeExpressions, treatmentRelativeExpressions);
  } catch (err) {
    // t検定に失敗した場合はnullを返す（サンプル数が少ない場合など）
    if (process.env.NODE_ENV !== 'production') {
      console.warn('t検定の実行に失敗しました:', err);
    }
  }

  return {
    control: {
      samples: controlSamples,
      statistics: {
        mean: controlStats.mean,
        sd: controlStats.stdDev,
        se: controlStats.stdError,
        confidenceInterval: controlStats.confidenceInterval,
      },
    },
    treatment: {
      samples: treatmentSamples,
      statistics: {
        mean: treatmentStats.mean,
        sd: treatmentStats.stdDev,
        se: treatmentStats.stdError,
        confidenceInterval: treatmentStats.confidenceInterval,
      },
    },
    tTest: tTestResult,
  };
}

